import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ProjectId } from 'api/models';
import { confirm } from 'components/atoms/modal';
import { OnStatusUpdateUserMention } from 'components/molecules/mention';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import {
  getUsers,
  GetUsersParams,
  useAddMembersToProject,
} from 'hooks/use-invite-user';
import { useUserNameGetter } from 'hooks/user-user-name';
import { logger } from 'services';

type UserId = number;

const useAddUserAsCollaborator = () => {
  const intl = useIntl();
  const getUserName = useUserNameGetter();
  const okText = intl.formatMessage({ id: 'mention_and_add_collaborator' });
  const cancelText = intl.formatMessage({ id: 'cancel' });

  return useCallback(
    async (value, hideAddCollabAlert) => {
      return new Promise<UserId[]>((resolve) => {
        if (hideAddCollabAlert) {
          return resolve(value);
        }

        const title = intl.formatMessage(
          {
            id: 'collaborator.invitation.confirm.title',
          },
          {
            user: getUserName(value),
          },
        );
        const content = intl.formatMessage(
          {
            id: 'collaborator.via.mention.confirm.content',
          },
          {
            emailId: <b>{getUserName(value) || ''}</b>,
          },
        );

        return confirm({
          title,
          content,
          cancelText,
          okText,
          onOk: async () => {
            try {
              return resolve([value]);
            } catch (error) {
              logger.error('Invite Request failed', { error });

              return resolve([]);
            }
          },
          onCancel: () => resolve([]),
        });
      });
    },
    [cancelText, okText, intl, getUserName],
  );
};

const useInviteUserToProjectAndAdAsCollaborator = (
  projectId: ProjectId,
  configOptions?: Pick<
    GetUsersParams,
    'confirmModalTextKeys' | 'confirmModalText'
  >,
): ((selected: UserId[]) => Promise<UserId[]>) => {
  const onOk = useAddMembersToProject();
  const {
    data: { notInProject },
  } = useProjectSquad(projectId);
  const intl = useIntl();
  const getUserName = useUserNameGetter();

  // I had to pass the username to the modal. since modal was built way down in these hooks
  // it's easier to repeat myself.
  return useCallback(
    async (value) => {
      const title = intl.formatMessage(
        {
          id: 'collaborator.invitation.confirm.title',
        },
        {
          user: getUserName(value[0]),
        },
      );

      return getUsers({
        value,
        intl,
        onOk,
        projectId,
        notInProjectUsersSource: notInProject,
        confirmModalTextKeys: configOptions?.confirmModalTextKeys,
        confirmModalText: {
          title,
        },
      });
    },
    [notInProject, intl, onOk, projectId, configOptions, getUserName],
  );
};

const useStatusUpdateSingleUserMentionHandler = (
  projectId: ProjectId,
): ((
  value: number | null,
  isSuggestionNotInProject?: boolean,
  hideAddCollabAlert?: boolean,
) => Promise<number | null>) => {
  const handleInviteCollaborate = useInviteUserToProjectAndAdAsCollaborator(
    projectId,
    {
      confirmModalTextKeys: {
        content: 'invitation.via.mention.confirm.content.with.collaborator',
        okText: 'invite_and_add_collaborator',
      },
    },
  );
  const handleAddCollaborate = useAddUserAsCollaborator();

  return useCallback(
    async (
      value: number | null,
      isSuggestionNotInProject?: boolean,
      hideAddCollabAlert?: boolean,
    ) => {
      if (value) {
        const handle = isSuggestionNotInProject
          ? handleInviteCollaborate
          : handleAddCollaborate;
        const result = await handle([value], hideAddCollabAlert);

        return result ? (result.length > 0 ? result[0] : null) : null;
      }

      return value;
    },
    [handleInviteCollaborate, handleAddCollaborate],
  );
};

export const useStatusUpdateUserMention = (
  projectId: ProjectId,
): OnStatusUpdateUserMention => {
  const handle = useStatusUpdateSingleUserMentionHandler(projectId);
  const {
    data: { notInProject },
  } = useProjectSquad(projectId);

  return async (selected, callback, { hideAddCollabAlert }) => {
    const isSuggestionNotInProject = notInProject.find(
      (user) => user.userId === selected.objectId,
    );

    const response = await handle(
      selected.objectId,
      !!isSuggestionNotInProject,
      !!hideAddCollabAlert,
    );

    if (response) {
      callback(selected);
    }
  };
};
